import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { DEFAULT_HOW_HEARD, RequestFormSettingsSchema, paths, type RequestFormSettings, type RequestServiceType } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call, CallError } from "../lib/callFunction";
import { textareaClass } from "./JobEditCard";

// Requests → Website form: what the public form asks (type of service, routed
// to a department; "how did you hear about us"), which websites may send
// requests, and the code a web designer pastes in. Admins save it.

const APP = "https://app.clevis.pro";
const lines = (s: string) =>
  s
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
const newId = (label: string) =>
  `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24) || "type"}-${Math.random().toString(36).slice(2, 6)}`;

export function embedSnippet(slug: string): string {
  return `<div id="clevis-request"></div>
<script>
(function () {
  var box = document.getElementById("clevis-request"), q = new URLSearchParams(location.search);
  var u = new URL("${APP}/request/${slug}");
  u.searchParams.set("embed", "1"); u.searchParams.set("source", "website"); u.searchParams.set("page", location.pathname);
  ["utm_source", "utm_medium", "utm_campaign"].forEach(function (k) { if (q.get(k)) u.searchParams.set(k, q.get(k)); });
  var f = document.createElement("iframe");
  f.src = u.toString(); f.title = "Request a quote"; f.style.cssText = "width:100%;border:0;min-height:760px";
  box.appendChild(f);
  window.addEventListener("message", function (e) {
    if (e.origin === "${APP}" && e.data && e.data.type === "clevis:height") f.style.height = e.data.height + "px";
  });
})();
</script>`;
}

export function developerNotes(slug: string, company: string, types: RequestServiceType[]): string {
  return `${company}: quote requests go into Clevis (their CRM).

Option 1, easiest: paste the embed code where the form should appear. Clevis keeps the
form's fields, spam protection and texting consent up to date.

Option 2, your own form: POST it to
  ${APP}/api/requests/${slug}
as JSON (fetch) or a regular HTML form (application/x-www-form-urlencoded).
Fields:
  name          required
  email, phone  at least one is required
  service_type  ${types.length ? types.map((t) => `"${t.label}"`).join(", ") : "(free text)"}
  address       project address
  details       required: what they need
  preferred_times, how_heard   optional
  kind          "estimate" (default) or "service"
  page          optional: the page the form is on
  utm_source, utm_medium, utm_campaign   optional: pass through from the landing URL
  website       a hidden, empty field (spam trap: real visitors never fill it)
  redirect      optional, plain HTML forms: your thank-you page URL
Answer: {"ok": true}, or {"ok": false, "error": "..."} with status 400/403/429.
Requests are accepted only from the website addresses listed in Clevis
(Requests → Website form); tell us yours (and any staging address).

If the form asks for a phone number, show this under it (texting consent):
"By providing your phone number, you agree to receive service-related text messages
from ${company} (appointment confirmations, technician on-the-way alerts, and invoice
notices) via Clevis. Message frequency varies. Message & data rates may apply. Reply
STOP to opt out, HELP for help. Your number is never shared with third parties for
marketing. Privacy Policy: https://clevis.pro/privacy.html  SMS Terms: https://clevis.pro/terms.html"`;
}

function CopyBox({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <button
          className="text-xs font-semibold text-brandorange-dark hover:text-brandorange"
          onClick={() => {
            void navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "copied ✓" : "copy"}
        </button>
      </div>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-steel/20 bg-white p-2 text-[11px] leading-snug text-ink">{text}</pre>
    </div>
  );
}

export function RequestFormSetup({
  tenantId,
  slug,
  company,
  departments,
  canEdit,
}: {
  tenantId: string;
  slug: string;
  company: string;
  departments: Array<{ id: string; name: string }>;
  canEdit: boolean;
}) {
  const [saved, setSaved] = useState<RequestFormSettings | null>(null);
  const [types, setTypes] = useState<RequestServiceType[]>([]);
  const [newType, setNewType] = useState("");
  const [askHowHeard, setAskHowHeard] = useState(true);
  const [howHeard, setHowHeard] = useState("");
  const [websites, setWebsites] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenantRequestFormSettings(tenantId)), (snap) => {
        const s = RequestFormSettingsSchema.parse(snap.data() ?? {});
        setSaved(s);
        setTypes(s.serviceTypes);
        setAskHowHeard(s.askHowHeard);
        setHowHeard(s.howHeardOptions.join("\n"));
        setWebsites(s.websiteOrigins.join("\n"));
      }),
    [tenantId],
  );

  async function save() {
    setBusy(true);
    setNote("");
    try {
      const res = await call<{ websiteOrigins: string[] }>("setRequestFormSettings", {
        tenantId,
        serviceTypes: types,
        askHowHeard,
        howHeardOptions: lines(howHeard).slice(0, 20),
        websites: lines(websites),
      });
      setNote(`Saved.${res.websiteOrigins.length ? ` Accepting requests from ${res.websiteOrigins.join(", ")}.` : ""}`);
    } catch (err) {
      const e = err as CallError;
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!saved) return null;
  const link = `${APP}/request/${slug}`;
  return (
    <Card className="space-y-3">
      <CardTitle className="mb-0">Website form</CardTitle>
      <p className="text-xs text-steel">
        What your quote form asks, and how to put it on your own website. Requests land here, sorted by department.
      </p>

      <div>
        <Label>Type of service (each goes to a department's queue)</Label>
        <div className="space-y-1">
          {types.map((t, i) => (
            <div key={t.id} className="flex items-center gap-2">
              <Input
                className="flex-1 py-1 text-sm"
                value={t.label}
                disabled={!canEdit}
                onChange={(e) => setTypes(types.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              />
              <Select
                className="w-44 py-1 text-sm"
                value={t.departmentId ?? ""}
                disabled={!canEdit}
                onChange={(e) => setTypes(types.map((x, j) => (j === i ? { ...x, departmentId: e.target.value || null } : x)))}
              >
                <option value="">No department</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
              {canEdit ? (
                <button className="text-xs text-steel hover:text-danger" onClick={() => setTypes(types.filter((_, j) => j !== i))}>
                  remove
                </button>
              ) : null}
            </div>
          ))}
          {canEdit ? (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const label = newType.trim();
                if (!label) return;
                setTypes([...types, { id: newId(label), label, departmentId: null }]);
                setNewType("");
              }}
            >
              <Input className="flex-1 py-1 text-sm" placeholder="e.g. Garage door repair, Fireplace service, New garage door" value={newType} onChange={(e) => setNewType(e.target.value)} />
              <Button type="submit" variant="ghost" className="px-2 py-1 text-xs">
                + Add
              </Button>
            </form>
          ) : null}
          {departments.length === 0 ? (
            <p className="text-[11px] text-steel">Add departments on the Team page (Fireplace, Garage Service…) to route each type to its own queue.</p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={askHowHeard} disabled={!canEdit} onChange={(e) => setAskHowHeard(e.target.checked)} />
            Ask "How did you hear about us?"
          </label>
          {askHowHeard ? (
            <textarea
              className={textareaClass}
              rows={5}
              disabled={!canEdit}
              placeholder={DEFAULT_HOW_HEARD.join("\n")}
              value={howHeard}
              onChange={(e) => setHowHeard(e.target.value)}
            />
          ) : null}
          <p className="text-[11px] text-steel">One answer per line. These are your lead sources (Setup → Lead sources keeps the same list, with tracking numbers).</p>
        </div>
        <div>
          <Label>Your website(s)</Label>
          <textarea className={textareaClass} rows={3} disabled={!canEdit} placeholder="csglv.com" value={websites} onChange={(e) => setWebsites(e.target.value)} />
          <p className="text-[11px] text-steel">Needed only if your web designer builds their own form. "csglv.com" covers www too.</p>
        </div>
      </div>

      {canEdit ? (
        <div className="flex items-center gap-2">
          <Button busy={busy} onClick={() => void save()}>
            Save website form
          </Button>
          {note ? <span className="text-xs text-steel">{note}</span> : null}
        </div>
      ) : null}

      <div className="space-y-2 rounded-lg bg-cream/60 p-3">
        <div className="text-sm font-semibold text-ink">Put it on your website</div>
        <p className="text-xs text-steel">
          Simplest: link a "Request a quote" button to{" "}
          <a href={link} target="_blank" rel="noreferrer" className="font-mono text-ink hover:text-brandorange">
            {link.replace("https://", "")}
          </a>
          . To show the form inside your site, your web designer pastes the embed code. Or send them the developer notes to
          build their own form that sends requests here.
        </p>
        <CopyBox label="Embed code (paste where the form goes)" text={embedSnippet(slug)} />
        <CopyBox label="Notes for your web designer" text={developerNotes(slug, company, types)} />
      </div>
    </Card>
  );
}
